import { useParams, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight, Check, MapPin, Phone, Mail, MessageCircle,
  Clock, Send, Loader2, CheckCircle, Home, ChevronRight, Star
} from "lucide-react";
import axios from "axios";
import { getLocationBySlug, locations } from "@/data/locations";
import { SITE_URL, OWNER_NAME, OWNER_PHONE, OWNER_EMAIL, OWNER_ADDRESS, HERO_IMAGE, LOCATION_HERO_IMAGE } from "@/lib/seo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationPage = () => {
  const { slug } = useParams();
  const location = getLocationBySlug(slug);

  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!location) {
    return <Navigate to="/" replace />;
  }

  const pageUrl = `${SITE_URL}/standort/${location.slug}`;
  const title = `Thermomix® ${location.name} | Kostenlose Vorführung & Beratung – Jennifer Laak`;
  const description = `Thermomix® TM7 in ${location.name} – kostenlose & unverbindliche Vorführung bei dir zuhause. Persönliche Beratung von Jennifer Laak. Erlebniskochen, Kauf, Finanzierung. Jetzt Termin sichern!`;
  const keywords = `Thermomix ${location.name}, Thermomix kaufen ${location.name}, Thermomix Vorführung ${location.name}, Thermomix Beratung ${location.name}, TM7 ${location.name}, Thermomix Repräsentantin ${location.name}, Erlebniskochen ${location.name}, ${location.stadtteile.slice(0, 4).map((s) => `Thermomix ${s}`).join(", ")}`;

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Startseite", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Standorte", "item": `${SITE_URL}/#standorte` },
      { "@type": "ListItem", "position": 3, "name": `Thermomix ${location.name}`, "item": pageUrl }
    ]
  };

  const localBusinessLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": pageUrl,
    "name": `Thermomix® Beratung ${location.name} – ${OWNER_NAME}`,
    "image": HERO_IMAGE,
    "description": description,
    "url": pageUrl,
    "telephone": OWNER_PHONE,
    "email": OWNER_EMAIL,
    "priceRange": "€€",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": OWNER_ADDRESS.street,
      "postalCode": OWNER_ADDRESS.postalCode,
      "addressLocality": OWNER_ADDRESS.city,
      "addressCountry": OWNER_ADDRESS.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": location.lat,
      "longitude": location.lng
    },
    "areaServed": {
      "@type": "City",
      "name": location.name,
      "containedInPlace": { "@type": "AdministrativeArea", "name": location.bundesland }
    },
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      "opens": "09:00",
      "closes": "18:00"
    }],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "3"
    }
  };

  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": `Thermomix Vorführung und Beratung in ${location.name}`,
    "provider": {
      "@type": "Person",
      "name": OWNER_NAME,
      "telephone": OWNER_PHONE,
      "email": OWNER_EMAIL
    },
    "areaServed": { "@type": "City", "name": location.name },
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR", "description": "Kostenlose und unverbindliche Thermomix-Vorführung bei dir zuhause" }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: `Anfrage aus ${location.name}${formData.message ? `\n\n${formData.message}` : "\n\n(Standort-Landingpage)"}`
      });
      toast.success("Vielen Dank für deine Anfrage!", {
        description: `Ich melde mich innerhalb von 24 Stunden bei dir in ${location.name}.`
      });
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Etwas ist schiefgelaufen.", { description: "Bitte versuche es erneut oder ruf direkt an." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const whatsappLink = `https://wa.me/491721697433?text=${encodeURIComponent(`Hallo Jennifer! Ich komme aus ${location.name} und interessiere mich für eine Thermomix-Vorführung.`)}`;

  // andere Standorte für Cross-Linking (SEO-Silo)
  const otherLocations = locations.filter((l) => l.slug !== location.slug).slice(0, 8);

  return (
    <div data-testid={`location-page-${location.slug}`} className="min-h-screen bg-stone-50">
      <Helmet>
        <html lang="de" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <meta name="geo.region" content="DE-NW" />
        <meta name="geo.placename" content={location.name} />
        <meta name="geo.position" content={`${location.lat};${location.lng}`} />
        <meta name="ICBM" content={`${location.lat}, ${location.lng}`} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={HERO_IMAGE} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={HERO_IMAGE} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessLd)}</script>
        <script type="application/ld+json">{JSON.stringify(serviceLd)}</script>
      </Helmet>

      <Navigation />

      {/* Breadcrumbs */}
      <div className="pt-24 pb-4 bg-stone-50">
        <div className="container-custom">
          <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center gap-2 text-sm text-slate-500">
            <Link to="/" className="hover:text-emerald-600 flex items-center gap-1 transition-colors">
              <Home className="w-4 h-4" /> Start
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/#standorte" className="hover:text-emerald-600 transition-colors">Standorte</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-900 font-semibold">{location.name}</span>
          </nav>
        </div>
      </div>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pb-24 md:pb-32 overflow-hidden">
        <div className="hero-bg" style={{ backgroundImage: `url('${HERO_IMAGE}')` }} />
        <div className="hero-overlay" />
        <div className="absolute top-1/4 right-10 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 left-10 w-96 h-96 bg-orange-300/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container-custom relative z-10 pt-8 md:pt-16">
          <div className="max-w-3xl">
            <div className="glass rounded-[2rem] p-8 md:p-14">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-6">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-emerald-700">
                  {location.distance_km === 0 ? `${location.name} · Hauptstandort` : `${location.name} · ${location.distance_km} km von Krefeld`}
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-6 font-[Outfit]">
                Thermomix® in
                <span className="text-gradient block mt-1">{location.name}.</span>
                <span className="block mt-2 text-3xl sm:text-4xl lg:text-5xl text-slate-900">Persönlich bei dir zuhause.</span>
              </h1>

              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                {location.hero_intro}
              </p>

              <div className="flex flex-wrap gap-x-6 gap-y-3 mb-10">
                {["Kostenlos & unverbindlich", "Bei dir zuhause", "Persönliche Beratung"].map((b, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center shadow-sm">
                      <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 font-medium">{b}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => { const el = document.getElementById("standort-kontakt"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
                  data-testid={`hero-cta-${location.slug}`}
                  className="btn-primary px-10 py-7 text-lg group"
                >
                  Termin in {location.name} sichern
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hero-whatsapp-${location.slug}`}
                  className="btn-whatsapp px-8 py-5 text-lg"
                >
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WARUM JENNIFER IN DER STADT */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4 block">
                Deine Expertin in {location.name}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 tracking-tight font-[Outfit]">
                Warum Thermomix® mit Jennifer in {location.name}?
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">{location.local_fact}</p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">{location.why_me}</p>
              <div className="space-y-4">
                {location.highlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-emerald-600" strokeWidth={3} />
                    </div>
                    <span className="text-slate-700 font-medium leading-relaxed">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="glass-card rounded-[2rem] p-8 md:p-10">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 font-[Outfit]">
                Stadtteile & Bereiche in {location.name}
              </h3>
              <p className="text-slate-600 mb-6">
                Ich komme zu dir – in jeden Stadtteil und jede Ecke von {location.name_dativ}:
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {location.stadtteile.map((st) => (
                  <span key={st} className="px-3 py-1.5 text-sm font-medium bg-emerald-50 text-emerald-700 rounded-full border border-emerald-100">
                    {st}
                  </span>
                ))}
              </div>

              {location.plz_list.length > 0 && (
                <div className="pt-6 border-t border-stone-200">
                  <div className="text-sm font-semibold text-slate-700 mb-3">Postleitzahlen:</div>
                  <div className="flex flex-wrap gap-1.5">
                    {location.plz_list.slice(0, 14).map((p) => (
                      <span key={p} className="px-2 py-0.5 text-xs font-mono bg-stone-100 text-slate-600 rounded">{p}</span>
                    ))}
                    {location.plz_list.length > 14 && (
                      <span className="px-2 py-0.5 text-xs font-mono text-slate-500">+{location.plz_list.length - 14} weitere</span>
                    )}
                  </div>
                </div>
              )}

              <div className="pt-6 mt-6 border-t border-stone-200 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Entfernung von Krefeld</div>
                  <div className="font-semibold text-slate-900">~ {location.drive_min} Minuten · {location.distance_km} km</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MAPS */}
      <section className="section-padding bg-stone-50">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4 block">Mein Einsatzgebiet</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight font-[Outfit]">
              Thermomix-Vorführung in {location.name}
            </h2>
            <p className="text-slate-600 text-lg">
              Von Krefeld nach {location.name} – {location.distance_km === 0 ? "ich wohne direkt hier" : `nur ${location.distance_km} km Anfahrt`}.
            </p>
          </div>

          <div className="max-w-5xl mx-auto glass-card rounded-[2rem] p-2 overflow-hidden">
            <iframe
              title={`Google Maps ${location.name}`}
              src={`https://maps.google.com/maps?q=${location.lat},${location.lng}&z=12&output=embed`}
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: "1.5rem" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid={`map-${location.slug}`}
            />
          </div>
        </div>
      </section>

      {/* FAQ spezifisch */}
      <section className="section-padding bg-white">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4 block">Häufige Fragen</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight font-[Outfit]">
              Thermomix® in {location.name} – FAQ
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: `Was kostet eine Thermomix-Vorführung in ${location.name}?`,
                a: `Absolut gar nichts. Die Beratung und das Erlebniskochen bei dir zuhause in ${location.name} ist komplett kostenlos und unverbindlich. Du gehst keinerlei Verpflichtungen ein.`
              },
              {
                q: `Kommt Jennifer Laak auch zu mir nach ${location.name}?`,
                a: `Ja, selbstverständlich! ${location.name} gehört zu meinem festen Einsatzgebiet im 25km-Umkreis von Krefeld. Die Anfahrt dauert nur etwa ${location.drive_min} Minuten.`
              },
              {
                q: `Kann ich den TM7 in ${location.name} live testen?`,
                a: `Ja! Bei meinem Erlebniskochen stehst du selbst an den Knöpfen. Du kochst ein kleines Gericht und kannst alle Funktionen des Thermomix® TM7 ausgiebig ausprobieren – direkt in deiner eigenen Küche.`
              },
              {
                q: `Gibt es Finanzierung bei Kauf in ${location.name}?`,
                a: `Ja, Vorwerk bietet attraktive Finanzierungsmodelle an – oft mit Aktionen zu 0% Zinsen. Ab ca. 50 € monatlich kannst du den TM7 finanzieren. Ich erkläre dir alle Optionen in Ruhe bei der Vorführung.`
              }
            ].map((faq, i) => (
              <details key={i} className="glass-card rounded-2xl p-6 group cursor-pointer">
                <summary className="font-semibold text-slate-900 flex items-center justify-between gap-4 list-none font-[Outfit] text-lg">
                  {faq.q}
                  <ChevronRight className="w-5 h-5 text-emerald-600 shrink-0 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>

          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": `Was kostet eine Thermomix-Vorführung in ${location.name}?`,
                "acceptedAnswer": { "@type": "Answer", "text": `Absolut gar nichts. Die Beratung und das Erlebniskochen bei dir zuhause in ${location.name} ist komplett kostenlos und unverbindlich.` }
              },
              {
                "@type": "Question",
                "name": `Kommt Jennifer Laak auch zu mir nach ${location.name}?`,
                "acceptedAnswer": { "@type": "Answer", "text": `Ja, ${location.name} gehört zu meinem festen Einsatzgebiet im 25km-Umkreis von Krefeld.` }
              }
            ]
          })}</script>
        </div>
      </section>

      {/* KONTAKT / CTA */}
      <section id="standort-kontakt" className="section-padding relative overflow-hidden bg-stone-50">
        <div className="container-custom relative z-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4 block">Jetzt Termin sichern</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight mb-4 font-[Outfit]">
              Deine Vorführung in {location.name}
            </h2>
            <p className="text-slate-600 text-lg">
              Schreib mir kurz – ich melde mich innerhalb von 24 Stunden persönlich bei dir.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-start max-w-5xl mx-auto">
            <div className="glass-card rounded-[2rem] p-8 md:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3 font-[Outfit]">Anfrage erhalten!</h3>
                  <p className="text-slate-600 mb-6">Ich melde mich innerhalb von 24 Stunden bei dir in {location.name}.</p>
                  <Button onClick={() => setIsSubmitted(false)} className="btn-secondary px-6 py-3">Weitere Nachricht senden</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid={`contact-form-${location.slug}`} className="space-y-5">
                  <h3 className="text-2xl font-bold text-slate-900 font-[Outfit] mb-2">Schnellanfrage aus {location.name}</h3>
                  <Input type="text" placeholder="Dein Name *" required data-testid={`contact-name-${location.slug}`}
                    value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="form-input" />
                  <Input type="email" placeholder="E-Mail Adresse *" required data-testid={`contact-email-${location.slug}`}
                    value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="form-input" />
                  <Input type="tel" placeholder="Telefon (optional)" data-testid={`contact-phone-${location.slug}`}
                    value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="form-input" />
                  <textarea rows={4} placeholder={`Deine Nachricht (optional) – z. B. Wunschtermin in ${location.name}`}
                    data-testid={`contact-message-${location.slug}`}
                    value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="form-input resize-none" />
                  <Button type="submit" data-testid={`contact-submit-${location.slug}`} disabled={isSubmitting}
                    className="w-full btn-primary py-6 text-lg disabled:opacity-50">
                    {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" />Wird gesendet...</>) : (<><Send className="w-5 h-5" />Anfrage senden</>)}
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Ich melde mich innerhalb von 24 Stunden. Deine Daten sind gemäß{" "}
                    <Link to="/datenschutz" className="text-emerald-600 hover:underline">Datenschutzerklärung</Link> sicher.
                  </p>
                </form>
              )}
            </div>

            <div className="space-y-5">
              <div className="glass-card rounded-[2rem] p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center mx-auto mb-5 shadow-lg shadow-green-500/20">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2 font-[Outfit]">Lieber schnell per WhatsApp?</h3>
                <p className="text-slate-600 mb-6">Ich antworte meist innerhalb weniger Stunden.</p>
                <a href={whatsappLink} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-${location.slug}`} className="btn-whatsapp w-full px-8 py-5 text-lg">
                  <MessageCircle className="w-5 h-5" />WhatsApp öffnen
                </a>
              </div>

              <div className="glass-card rounded-[2rem] p-8">
                <h3 className="text-xl font-bold text-slate-900 mb-6 font-[Outfit]">Direkter Kontakt</h3>
                <div className="space-y-3">
                  <a href="tel:+491721697433" className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 hover:bg-emerald-50 transition-all">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">Anrufen</div>
                      <div className="font-semibold text-slate-900">0172 1697433</div>
                    </div>
                  </a>
                  <a href={`mailto:${OWNER_EMAIL}`} className="flex items-center gap-4 p-4 rounded-2xl bg-white/60 hover:bg-emerald-50 transition-all">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-sm text-slate-500">E-Mail</div>
                      <div className="font-semibold text-slate-900">{OWNER_EMAIL}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CROSS-LINKING zu anderen Standorten (SEO-Silo) */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4 block">Weitere Standorte</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4 font-[Outfit]">
              Thermomix® in deiner Region
            </h2>
            <p className="text-slate-600 text-lg">
              Auch in deinen Nachbarorten bin ich gerne für dich da.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {otherLocations.map((l) => (
              <Link
                key={l.slug}
                to={`/standort/${l.slug}`}
                data-testid={`cross-link-${l.slug}`}
                className="glass-card rounded-2xl p-5 hover:bg-emerald-50/50 hover:border-emerald-200 transition-all group"
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-slate-500 font-medium">{l.distance_km} km</span>
                </div>
                <div className="font-bold text-slate-900 font-[Outfit] group-hover:text-emerald-700 transition-colors">
                  Thermomix {l.name}
                </div>
                <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600 font-semibold">
                  Details <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationPage;
